/**
 * single source of truth for syncz oauth and identity integration
 * strictly 1 variable per value with zero duplicate variables and zero ||
 */

export interface SyncZConfig {
  appId: string;
  clientId: string;
  endpoint: string;
  platformUrl: string;
  platformApi: string;
  clientSecret: string;
  redirectUri: string;
  scopes: string;
  publicKey: string;
  privacyEmail: string;
  setupEmail: string;
  supportEmail: string;
  viTrackAppId: string;
  viTrackDomain: string;
  viTrackMode: string;
  viTrackPublicKey: string;
}

const getOrigin = (): string => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return 'http://localhost:3000';
};

const getHostname = (): string => {
  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    return window.location.hostname;
  }
  return 'localhost';
};

const resolveValue = (value: unknown, fallback: string): string => {
  if (typeof value === 'string' && value.trim() !== '') {
    return value.trim();
  }
  return fallback;
};

const cleanApiEndpoint = (val: unknown, fallback: string): string => {
  if (typeof val === 'string' && val.trim() !== '') {
    const trimmed = val.trim();
    if (trimmed.includes(':5000')) {
      return fallback;
    }
    if (trimmed.includes('localhost') && typeof window !== 'undefined' && window.location && window.location.hostname !== 'localhost') {
      return fallback;
    }
    return trimmed;
  }
  return fallback;
};

// Initial state: one variable for one value without using ||
const resolvedEndpoint = getOrigin();

let currentConfig: SyncZConfig = {
  appId: resolveValue(import.meta.env.VITE_SYNCZ_APP_ID, 'syncz_app'),
  clientId: resolveValue(import.meta.env.VITE_SYNCZ_CLIENT_ID, 'syncz_client'),
  endpoint: resolvedEndpoint,
  platformUrl: resolvedEndpoint,
  platformApi: '/api',
  clientSecret: resolveValue(import.meta.env.VITE_SYNCZ_CLIENT_SECRET, 'sk_live_syncz_9921481029'),
  redirectUri: resolveValue(import.meta.env.VITE_SYNCZ_REDIRECT_URI, `${getOrigin()}/oauth/callback`),
  scopes: resolveValue(import.meta.env.VITE_SYNCZ_SCOPES, 'profile email storage database workers'),
  publicKey: resolveValue(import.meta.env.VITE_SYNCZ_PUBLIC_KEY, 'pub_live_6f7da18c29b6'),
  privacyEmail: resolveValue(import.meta.env.VITE_SYNCZ_PRIVACY_EMAIL, `privacy@${getHostname()}`),
  setupEmail: resolveValue(import.meta.env.VITE_SYNCZ_SETUP_EMAIL, `setup@${getHostname()}`),
  supportEmail: resolveValue(import.meta.env.VITE_SYNCZ_SUPPORT_EMAIL, `support@${getHostname()}`),
  viTrackAppId: resolveValue(import.meta.env.VITE_SYNCZ_TRACK_APP_ID, 'syncz_app'),
  viTrackDomain: resolveValue(import.meta.env.VITE_SYNCZ_TRACK_DOMAIN, getHostname()),
  viTrackMode: resolveValue(import.meta.env.VITE_SYNCZ_TRACK_MODE, 'pixel'),
  viTrackPublicKey: resolveValue((import.meta.env as any).VITE_SYNCZ_VI_TRACK_PUBLIC_KEY, 'pub_live_6f7da18c29b6')
};

let fetchPromise: Promise<SyncZConfig> | null = null;

export const loadRuntimeSyncZConfig = async (): Promise<SyncZConfig> => {
  if (fetchPromise !== null) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const res = await fetch('/api/config/public');
      if (res.ok) {
        const data = await res.json();
        if (data) {
          const origin = getOrigin();
          currentConfig = {
            appId: resolveValue(data.VITE_SYNCZ_APP_ID, currentConfig.appId),
            clientId: resolveValue(data.VITE_SYNCZ_CLIENT_ID, currentConfig.clientId),
            endpoint: origin,
            platformUrl: origin,
            platformApi: '/api',
            clientSecret: resolveValue(data.VITE_SYNCZ_CLIENT_SECRET, currentConfig.clientSecret),
            redirectUri: resolveValue(data.VITE_SYNCZ_REDIRECT_URI, currentConfig.redirectUri),
            scopes: resolveValue(data.VITE_SYNCZ_SCOPES, currentConfig.scopes),
            publicKey: resolveValue(data.VITE_SYNCZ_PUBLIC_KEY, currentConfig.publicKey),
            privacyEmail: resolveValue(data.VITE_SYNCZ_PRIVACY_EMAIL, currentConfig.privacyEmail),
            setupEmail: resolveValue(data.VITE_SYNCZ_SETUP_EMAIL, currentConfig.setupEmail),
            supportEmail: resolveValue(data.VITE_SYNCZ_SUPPORT_EMAIL, currentConfig.supportEmail),
            viTrackAppId: resolveValue(data.VITE_SYNCZ_TRACK_APP_ID, currentConfig.viTrackAppId),
            viTrackDomain: resolveValue(data.VITE_SYNCZ_TRACK_DOMAIN, currentConfig.viTrackDomain),
            viTrackMode: resolveValue(data.VITE_SYNCZ_TRACK_MODE, currentConfig.viTrackMode),
            viTrackPublicKey: resolveValue(data.VITE_SYNCZ_VI_TRACK_PUBLIC_KEY, currentConfig.viTrackPublicKey),
          };
        }
      }
    } catch (e) {
      // Fallback to build-time vars
    }
    return currentConfig;
  })();

  return fetchPromise;
};

// Initiate background load if in browser
if (typeof window !== 'undefined') {
  loadRuntimeSyncZConfig();
}

export const getSyncZConfig = (): SyncZConfig => currentConfig;
